import {
  Coupon,
  CouponInfoSheet,
  WalletCoupon,
} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/coupon.types';
import {LinkTarget} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/link_target.types';
import {Product} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/product.types';
import {TrackingPromotionCreative} from '@aboutyou/legacy-tracking-sdk';
import {useHistory} from '@aboutyou/router';
import {TrackPromotions} from '@aboutyou/tracking';
import React, {FC, PropsWithChildren, memo} from 'react';
import styled from 'styled-components';
import {CouponCard} from '../../../CouponCard/CouponCard';
import {getIsExpiryDateLessThan} from '../../../CouponCard/helpers/getIsExpiryDateLessThan';
import {getCouponExpiryDate} from '../../getCouponExpiryDate';
import {getCouponPromotionId} from '../../getCouponPromotionId';
import {COUPON_PROMOTION_ID_QUERY_KEY} from '../../useCouponPromotionIdFromQueryParams';
import {hasAppOnlyRedeemableDevices} from '../../utils/hasAppOnlyRedeemableDevices';

interface CouponCardWithLinkProps {
  coupon: WalletCoupon;
  enableEmptyDisabledView?: boolean;
  handleInfoIconClick: (couponInfo?: CouponInfoSheet) => void;
  setSelectedProduct?: (product: Product | undefined) => void;
  stateManagerCustomKey?: string;
  onTouchStart?: VoidFunction;
  onClick?: VoidFunction;
  className?: string;
}

const CouponCardWithLink: FC<CouponCardWithLinkProps> = ({
  coupon: walletCoupon,
  enableEmptyDisabledView,
  handleInfoIconClick,
  setSelectedProduct,
  stateManagerCustomKey,
  onClick,
  onTouchStart,
  className,
}) => {
  const {coupon, linkTarget, tracker} = walletCoupon;

  return (
    <CouponWrapper
      linkTarget={getCouponLink(coupon, linkTarget)}
      tracker={tracker}
      enableEmptyDisabledView={enableEmptyDisabledView}
      couponId={getCouponPromotionId(coupon) || ''}
      onTouchStart={onTouchStart}
      onClick={onClick}
      className={className}
      couponExpiryDate={getCouponExpiryDate(coupon)}
    >
      <StyledCouponCard
        coupon={coupon}
        onInfoIconClick={handleInfoIconClick}
        stateManagerCustomKey={stateManagerCustomKey}
        isDisabled={enableEmptyDisabledView}
        isDisplayMode={true}
        setSelectedProduct={setSelectedProduct}
        productId={getHotDealCouponProductId(coupon)}
        isInWallet
      />
    </CouponWrapper>
  );
};

const StyledCouponCard = styled(CouponCard)`
  height: 100%;
`;

const CouponCardWithLinkMemo = memo(CouponCardWithLink);

export {CouponCardWithLinkMemo as CouponCardWithLink};

interface CouponWrapperProps {
  enableEmptyDisabledView?: boolean;
  linkTarget: string;
  tracker: WalletCoupon['tracker'];
  couponId: string;
  onTouchStart?: VoidFunction;
  onClick?: VoidFunction;
  className?: string;
  couponExpiryDate?: Date;
}

const CouponWrapper: React.FC<PropsWithChildren<CouponWrapperProps>> = ({
  enableEmptyDisabledView,
  linkTarget,
  tracker,
  children,
  couponId,
  onClick,
  onTouchStart,
  className,
  couponExpiryDate,
}) => {
  const {push} = useHistory();

  if (enableEmptyDisabledView) {
    return children;
  }

  const handleOnClick = () => {
    onClick?.();
    push(linkTarget);
  };

  const isExpiryDateLessThan12Hours = getIsExpiryDateLessThan({
    expiryDate: couponExpiryDate,
    unit: 'hours',
    threshold: 12,
  });

  const isExpiryDateLessThan5Days = getIsExpiryDateLessThan({
    expiryDate: couponExpiryDate,
    unit: 'days',
    threshold: 5,
  });

  const promotionName =
    isExpiryDateLessThan5Days || isExpiryDateLessThan12Hours
      ? `${tracker.promotionName}|urgent`
      : tracker.promotionName;

  return (
    <div
      data-testid={`couponLink-${couponId}`}
      onClick={handleOnClick}
      onTouchStart={onTouchStart}
      className={className}
    >
      <StyledTrackPromotions
        data-testid="couponCard-promotionTracking"
        promotion={{
          source: TrackingPromotionCreative.APIPromotion,
          apiPromotion: {...tracker, promotionName},
        }}
        streamPosition={0}
      >
        {children}
      </StyledTrackPromotions>
    </div>
  );
};

const StyledTrackPromotions = styled(TrackPromotions)`
  height: 100%;
`;

// coupon Returns ADP link for hot deal coupons or coupon landing page link for the rest
const getCouponLink = (coupon: Coupon, couponLinkTarget: LinkTarget) => {
  if (coupon.type?.$case === 'hotDealCoupon') {
    const hotDealCoupon = coupon.type.hotDealCoupon;

    const isOnlyAppCoupon = hasAppOnlyRedeemableDevices(
      hotDealCoupon.redeemableDevices,
    );

    const queryParamCouponId = `?${COUPON_PROMOTION_ID_QUERY_KEY}=${coupon.type.hotDealCoupon.couponPromotionId}`;
    return `${
      coupon.type.hotDealCoupon.productAndBrandInformation.product.path
    }${isOnlyAppCoupon ? '' : queryParamCouponId}`;
  } else {
    return couponLinkTarget.url;
  }
};

const getHotDealCouponProductId = (coupon: Coupon) => {
  if (coupon.type?.$case === 'hotDealCoupon') {
    return coupon.type?.hotDealCoupon.productAndBrandInformation.product.id;
  }

  return undefined;
};
